import React from 'react'
import { FC } from 'react'
import { css } from '@emotion/react'
import { Link } from 'gatsby'
import LogoFooter from '@images/LogoFooter'
import { MyTheme, theme } from '@ui/styles/theme'
import { useTranslation } from 'react-i18next'
import { buildLangPath } from '@lib/pathUtils'
import { Languages } from '@/types/languages'

type Props = {
  lang: Languages
}

const Address: FC<{
  lang: Languages
  postCode: string
  address: string
  buildingName: string
}> = ({ lang, postCode, address, buildingName }) => {
  if (lang === 'ja') {
    return (
      <>
        <p>{postCode}</p>
        <p>{address}</p>
        <p>{buildingName}</p>
      </>
    )
  } else {
    return (
      <>
        <p>{buildingName}</p>
        <p>{address}</p>
        <p>{postCode}</p>
      </>
    )
  }
}

const Footer: FC<Props> = ({ lang }) => {
  const styles = createStyles(theme)
  const { t } = useTranslation()
  const { i18n } = useTranslation()

  const contactUs =
    lang === 'ja'
      ? 'https://docs.google.com/forms/d/e/1FAIpQLSdAny1Z3ptdIgVrTc4o2JTg-SIYf2rLGzvfoGhNA7-Fwk4mug/viewform'
      : 'https://docs.google.com/forms/d/1CFD7H7JjVB9kV902VUj3p9OeRaDHCiX89dkLIx8WMrU/viewform'

  const currentYear = new Date().getFullYear()

  return (
    <footer css={styles.footer}>
      <div css={styles.footerInner}>
        <div css={styles.footerLogo}>
          <Link to={buildLangPath('/', lang)}>
            <LogoFooter />
          </Link>
        </div>
        <div css={styles.footerInfo}>
          <p css={styles.footerInfoTitle}>
            <a href={contactUs} target="_blank" rel="noreferrer">
              {t('お問い合わせ')}
            </a>
          </p>
          <Address
            lang={i18n.language as Languages}
            postCode={t('105-0023')}
            address={t('東京都港区芝浦1丁目1-1')}
            buildingName={t('浜松町ビルディング11階')}
          />
          <small
            css={styles.footerInfoCopyright}
          >{`©️ ${currentYear} Aldagram Inc.`}</small>
        </div>
      </div>
    </footer>
  )
}

const createStyles = (theme: MyTheme) => {
  return {
    footer: css`
      display: flex;
      background: ${theme.colors.background.innermost};
      width: 100%;
      justify-content: start;
      ${theme.mq.isTablet} {
      }
    `,
    footerInner: css`
      display: flex;
      margin: 32px;
    `,
    footerLogo: css`
      width: auto;
      height: 77px;
      margin-right: 32px;
      vertical-align: top;
    `,
    footerInfo: css`
      font-size: 12px;
      line-height: 17px;
      vertical-align: top;
    `,
    footerInfoTitle: css`
      margin-bottom: 14px;
      color: ${theme.colors.primary};
      font-size: 14px;
      line-height: 20px;
      & > a {
        color: ${theme.colors.primary};
        &:hover {
          color: ${theme.colors.primaryDark};
        }
      }
    `,
    footerInfoPostcode: css`
      color: ${theme.colors.textBlack};
    `,
    footerInfoCopyright: css`
      display: inline-block;
      font-size: 11px;
      margin-top: 8px;
      line-height: 16px;
      opacity: 0.4;
    `
  }
}

export default Footer
